/**
 * EnemyShipDialog -- View a single enemy ship's details (components, crew).
 *
 * Data fetched from /api/profile/player/:userId/ships/:shipId.
 * Opens via EventBus 'open-enemy-ship' with { userId: string, shipId: string }.
 */

import Phaser from 'phaser';
import {
  getResponsiveDialogSize,
  getResponsiveFontSize,
  createScrollableContainer,
  isMobile,
} from '../../utils/ResponsiveHelper';
import { I18n } from '../../i18n/I18nService';

// ---- Colour & layout constants ----

const DESKTOP_WIDTH = 500;
const DESKTOP_HEIGHT = 520;
const BG_COLOR = 0x0f1a2e;
const BORDER_COLOR = 0x3366aa;
const HEADER_COLOR = 0x1a2a4e;
const SECTION_HEADER_COLOR = 0x192844;

// ---- Response interfaces ----

interface ComponentInfo {
  id: string;
  name: string;
  category: string;
  currentTier: number;
  stats: Record<string, number> | null;
  description: string;
}

interface ShipDetail {
  ship: {
    id: string;
    unitType: string;
    currentHp: number;
    maxHp: number;
    shields: number;
    maxShields: number;
    position: { q: number; r: number };
    createdAt: string;
  };
  components: ComponentInfo[];
  crew: any | null;
}

function unitDisplayName(type: string): string {
  return I18n.shipName(type);
}

// ====================================================================
// EnemyShipDialog
// ====================================================================

export class EnemyShipDialog extends Phaser.GameObjects.Container {
  private targetUserId: string;
  private targetShipId: string;
  private shipDetail: ShipDetail | null = null;
  private dims: ReturnType<typeof getResponsiveDialogSize>;

  private scrollInner: Phaser.GameObjects.Container | null = null;
  private scrollUpdate: (() => void) | null = null;
  private statusText: Phaser.GameObjects.Text;

  constructor(scene: Phaser.Scene, userId: string, shipId: string) {
    const dims = getResponsiveDialogSize(DESKTOP_WIDTH, DESKTOP_HEIGHT, scene);
    super(scene, dims.x, dims.y);
    this.dims = dims;
    this.targetUserId = userId;
    this.targetShipId = shipId;

    const { width, height } = scene.cameras.main;
    const dw = dims.width;
    const dh = dims.height;

    // Dark overlay
    const overlay = scene.add.rectangle(-dims.x, -dims.y, width, height, 0x000000, 0.7);
    overlay.setInteractive();
    overlay.on('pointerdown', () => this.close());
    this.add(overlay);

    // Main panel
    const bg = scene.add.rectangle(0, 0, dw, dh, BG_COLOR, 1);
    bg.setOrigin(0, 0);
    bg.setInteractive();
    this.add(bg);

    // Border
    const border = scene.add.graphics();
    border.lineStyle(2, BORDER_COLOR, 1);
    border.strokeRect(0, 0, dw, dh);
    this.add(border);

    // Header
    const headerBg = scene.add.rectangle(0, 0, dw, 50, HEADER_COLOR, 1);
    headerBg.setOrigin(0, 0);
    this.add(headerBg);

    const title = scene.add.text(dw / 2, 25, I18n.t('dialog.ship_details'), {
      fontSize: `${getResponsiveFontSize(20, scene)}px`,
      color: '#44aaff',
      fontFamily: 'Orbitron, Arial',
      fontStyle: 'bold',
    });
    title.setOrigin(0.5);
    this.add(title);

    // Close button — hugged to the top-right corner, mobile-sized hit area.
    this.add(this.createCloseButton(dw));

    // Scrollable content
    const listTop = 55;
    const listBottom = 25;
    const listHeight = dh - listTop - listBottom;
    const { container, updateScrollBounds } = createScrollableContainer(scene, this, {
      x: 0, y: listTop, width: dw, height: listHeight,
    });
    this.scrollInner = container;
    this.scrollUpdate = updateScrollBounds;

    // Status text
    this.statusText = scene.add.text(dw / 2, dh - 18, '', {
      fontSize: `${getResponsiveFontSize(12, scene)}px`,
      color: '#889999',
      fontFamily: 'Share Tech Mono, monospace',
    });
    this.statusText.setOrigin(0.5);
    this.add(this.statusText);

    this.setDepth(2000);
    scene.add.existing(this);
    this.loadShipDetail();
  }

  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('crimsonmandate_token');
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }

  private setStatus(msg: string): void { this.statusText.setText(msg); }

  private createCloseButton(dw: number): Phaser.GameObjects.Container {
    // Responsive sizing — larger hit target on mobile (44x44+ recommended for
    // touch), trim to a tighter visual on desktop.
    //
    // IMPORTANT: use TOP-LEFT origin for every child + the hit rectangle so
    // that the visible button and its collider are literally the same box.
    // Phaser Container.setInteractive anchors the hit Rectangle at the
    // container's local (0,0) — mixing that with center-origin children
    // causes the collider to drift above the visible button (the user had to
    // tap the upper edge for the X to register).
    const mobile = isMobile(this.scene);
    const SIZE = mobile ? 44 : 36;
    const FONT = mobile ? 22 : 18;
    const PAD  = 6;   // gap from dialog corner
    // Top-left of the button, in dialog-local coords.
    const bx = dw - PAD - SIZE;
    const by = PAD;

    const button = this.scene.add.container(bx, by);
    // Visible background — anchored at container-local (0,0) using top-left origin.
    const bg = this.scene.add.rectangle(0, 0, SIZE, SIZE, 0x660000);
    bg.setOrigin(0, 0);
    bg.setStrokeStyle(2, 0xff4444);
    button.add(bg);
    // Label centered inside the bg.
    const text = this.scene.add.text(SIZE / 2, SIZE / 2, 'X', {
      fontSize: `${FONT}px`, color: '#ffffff', fontFamily: 'Arial', fontStyle: 'bold',
    });
    text.setOrigin(0.5);
    button.add(text);
    button.setSize(SIZE, SIZE);
    // Hit area = visible bg (both at container-local (0,0) with same SIZE).
    button.setInteractive(
      new Phaser.Geom.Rectangle(0, 0, SIZE, SIZE),
      Phaser.Geom.Rectangle.Contains,
    );
    button.on('pointerover', () => bg.setFillStyle(0x880000));
    button.on('pointerout', () => bg.setFillStyle(0x660000));
    button.on('pointerdown', () => this.close());
    return button;
  }

  private close(): void { this.destroy(); }

  // ================================================================== Data

  private async loadShipDetail(): Promise<void> {
    this.setStatus('Loading ship detail...');
    try {
      const res = await fetch(`/api/profile/player/${this.targetUserId}/ships/${this.targetShipId}`, {
        headers: this.getAuthHeaders(),
      });
      const json = await res.json();
      if (!json.success) { this.setStatus('Failed to load ship detail.'); return; }
      this.shipDetail = json.data as ShipDetail;
      this.setStatus('');
      this.renderDetail();
    } catch (err) {
      console.error('[EnemyShipDialog] loadShipDetail error:', err);
      this.setStatus('Network error. Try again later.');
    }
  }

  // ================================================================== Render

  private renderDetail(): void {
    if (!this.scrollInner || !this.shipDetail) return;
    this.scrollInner.removeAll(true);

    const { ship, components, crew } = this.shipDetail;
    const dw = this.dims.width;
    let yOffset = 8;

    // Ship info
    const infoBg = this.scene.add.rectangle(10, yOffset, dw - 20, 70, 0x111e33, 1);
    infoBg.setOrigin(0, 0);
    infoBg.setStrokeStyle(1, BORDER_COLOR, 0.4);
    this.scrollInner.add(infoBg);

    const typeName = unitDisplayName(ship.unitType);
    const line1 = `Type: ${typeName}   |   HP: ${ship.currentHp}/${ship.maxHp}   |   Shields: ${ship.shields}/${ship.maxShields}`;
    const line1Text = this.scene.add.text(20, yOffset + 14, line1, {
      fontSize: `${getResponsiveFontSize(12, this.scene)}px`,
      color: '#ccddee',
      fontFamily: 'Share Tech Mono, monospace',
    });
    this.scrollInner.add(line1Text);

    const posStr = `(${ship.position.q}, ${ship.position.r})`;
    const createdStr = new Date(ship.createdAt).toLocaleDateString();
    const line2 = `Position: ${posStr}   |   Created: ${createdStr}`;
    const line2Text = this.scene.add.text(20, yOffset + 34, line2, {
      fontSize: `${getResponsiveFontSize(12, this.scene)}px`,
      color: '#8899bb',
      fontFamily: 'Share Tech Mono, monospace',
    });
    this.scrollInner.add(line2Text);

    const shipIdText = this.scene.add.text(20, yOffset + 52, `ID: ${ship.id}`, {
      fontSize: `${getResponsiveFontSize(10, this.scene)}px`,
      color: '#556677',
      fontFamily: 'Share Tech Mono, monospace',
    });
    this.scrollInner.add(shipIdText);
    yOffset += 80;

    // Components
    const compHeaderBg = this.scene.add.rectangle(10, yOffset, dw - 20, 26, SECTION_HEADER_COLOR, 1);
    compHeaderBg.setOrigin(0, 0);
    this.scrollInner.add(compHeaderBg);

    const compHeader = this.scene.add.text(20, yOffset + 13, 'COMPONENTS', {
      fontSize: `${getResponsiveFontSize(12, this.scene)}px`,
      color: '#44aaff',
      fontFamily: 'Orbitron, Arial',
      fontStyle: 'bold',
    });
    compHeader.setOrigin(0, 0.5);
    this.scrollInner.add(compHeader);
    yOffset += 32;

    if (components.length === 0) {
      const noComp = this.scene.add.text(25, yOffset, '[empty slots]', {
        fontSize: `${getResponsiveFontSize(12, this.scene)}px`,
        color: '#556677',
        fontFamily: 'Share Tech Mono, monospace',
      });
      this.scrollInner.add(noComp);
      yOffset += 20;
    } else {
      for (const comp of components) {
        const statsEntries = comp.stats ? Object.entries(comp.stats) : [];
        const statStr = statsEntries.map(([k, v]) => `${v} ${k}`).join(', ');
        const compLine = `* ${comp.name} Mk${comp.currentTier} (${comp.category})${statStr ? ' - ' + statStr : ''}`;
        const compText = this.scene.add.text(25, yOffset, compLine, {
          fontSize: `${getResponsiveFontSize(11, this.scene)}px`,
          color: '#aabbcc',
          fontFamily: 'Share Tech Mono, monospace',
          wordWrap: { width: dw - 60 },
        });
        this.scrollInner.add(compText);
        yOffset += compText.height + 4;
      }
    }
    yOffset += 8;

    // Crew
    const crewHeaderBg = this.scene.add.rectangle(10, yOffset, dw - 20, 26, SECTION_HEADER_COLOR, 1);
    crewHeaderBg.setOrigin(0, 0);
    this.scrollInner.add(crewHeaderBg);

    const crewHeader = this.scene.add.text(20, yOffset + 13, 'CREW', {
      fontSize: `${getResponsiveFontSize(12, this.scene)}px`,
      color: '#44aaff',
      fontFamily: 'Orbitron, Arial',
      fontStyle: 'bold',
    });
    crewHeader.setOrigin(0, 0.5);
    this.scrollInner.add(crewHeader);
    yOffset += 32;

    if (!crew) {
      const noCrew = this.scene.add.text(25, yOffset, 'No crew assigned', {
        fontSize: `${getResponsiveFontSize(12, this.scene)}px`,
        color: '#556677',
        fontFamily: 'Share Tech Mono, monospace',
      });
      this.scrollInner.add(noCrew);
    } else {
      const crewNameLine = `${crew.name}  (Lv.${crew.level})  -  ${crew.status}`;
      const crewNameText = this.scene.add.text(25, yOffset, crewNameLine, {
        fontSize: `${getResponsiveFontSize(13, this.scene)}px`,
        color: '#ccddff',
        fontFamily: 'Share Tech Mono, monospace',
        fontStyle: 'bold',
      });
      this.scrollInner.add(crewNameText);
      yOffset += 20;

      const barW = Math.min(200, dw * 0.4);
      const skills: { label: string; value: number; color: number }[] = [
        { label: 'Combat', value: crew.combat_skill, color: 0xff4444 },
        { label: 'Piloting', value: crew.piloting_skill, color: 0x44aaff },
        { label: 'Navigation', value: crew.navigation_skill, color: 0x44ff88 },
        { label: 'Engineering', value: crew.engineering_skill, color: 0xffaa44 },
        { label: 'Science', value: crew.science_skill, color: 0xcc66ff },
      ];

      for (const skill of skills) {
        const skillLabel = this.scene.add.text(30, yOffset, `${skill.label}:`, {
          fontSize: `${getResponsiveFontSize(11, this.scene)}px`,
          color: '#8899bb',
          fontFamily: 'Share Tech Mono, monospace',
        });
        this.scrollInner.add(skillLabel);

        const valText = this.scene.add.text(130, yOffset, `${skill.value}`, {
          fontSize: `${getResponsiveFontSize(11, this.scene)}px`,
          color: '#ccddee',
          fontFamily: 'Share Tech Mono, monospace',
        });
        this.scrollInner.add(valText);

        const barX = 160;
        const barH = 8;
        const barBg = this.scene.add.rectangle(barX, yOffset + 2, barW, barH, 0x222233);
        barBg.setOrigin(0, 0);
        this.scrollInner.add(barBg);

        const fillW = Math.max(1, (skill.value / 100) * barW);
        const barFill = this.scene.add.rectangle(barX, yOffset + 2, fillW, barH, skill.color);
        barFill.setOrigin(0, 0);
        this.scrollInner.add(barFill);
        yOffset += 16;
      }
    }
    this.scrollUpdate?.();
  }
}
